'use client';

import { FC, useEffect, useState, useMemo, useCallback } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { DevsuLogoComplete } from '@/assets/DevsuLogoComplete';
import { HamburgerIcon, CloseIcon } from '@/assets';

// Type for sibling slices passed via context
interface NavigationBarContext {
  slices?: Array<{ slice_type: string; id?: string }>;
}

/**
 * Props for `NavigationBar`.
 */
export type NavigationBarProps = SliceComponentProps<
  Content.NavigationBarSlice,
  NavigationBarContext
>;

/**
 * Component for "NavigationBar" Slices.
 */
const NavigationBar: FC<NavigationBarProps> = ({ slice, context }) => {
  const { logo, primary_action } = slice.primary;
  const [isSticky, setIsSticky] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Get custom labels from slice primary data
  const getLabel = useCallback(
    (key: string, defaultValue: string): string => {
      const value = (slice.primary as Record<string, unknown>)[key];
      return typeof value === 'string' && value ? value : defaultValue;
    },
    [slice.primary],
  );

  // Determine which slices are present on the page
  const siblingSlices = useMemo(() => context?.slices || [], [context?.slices]);

  // Build navigation links based on which slices are present
  const navLinks = useMemo(() => {
    const presentSliceTypes = new Set(siblingSlices.map((s) => s.slice_type));

    // Special handling: only show certain links if their corresponding slice exists
    const links: Array<{ label: string; sliceType: string; id: string }> = [];

    // Welcome - ProfileGreetingCta
    if (presentSliceTypes.has('profile_greeting_cta')) {
      links.push({
        label: getLabel('welcome_label', 'Welcome'),
        sliceType: 'profile_greeting_cta',
        id: 'profile_greeting_cta',
      });
    }

    // Video - FeaturedVideo
    if (presentSliceTypes.has('featured_video')) {
      links.push({
        label: getLabel('video_label', 'Video'),
        sliceType: 'featured_video',
        id: 'featured_video',
      });
    }

    // Deck - ResourceList (first occurrence)
    if (presentSliceTypes.has('resource_list')) {
      links.push({
        label: getLabel('deck_label', 'Deck'),
        sliceType: 'resource_list',
        id: 'resource_list',
      });
    }

    // Content - This could be a second resource_list or other content section
    // For now, we'll check if there's content that warrants this link
    // You can customize this based on your content structure

    // Case studies - CaseStudiesCarousel (testimonial_carousel_2)
    if (presentSliceTypes.has('testimonial_carousel_2')) {
      links.push({
        label: getLabel('case_studies_label', 'Case studies'),
        sliceType: 'testimonial_carousel_2',
        id: 'testimonial_carousel_2',
      });
    }

    return links;
  }, [siblingSlices, getLabel]);

  // Handle sticky behavior
  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle active section detection
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sliceType = entry.target.getAttribute('data-slice-type');
          if (sliceType) {
            setActiveSection(sliceType);
          }
        }
      });
    }, observerOptions);

    // Observe all sections that we have links for
    navLinks.forEach((link) => {
      const section = document.querySelector(
        `[data-slice-type="${link.sliceType}"]`,
      );
      if (section) {
        observer.observe(section);
      }
    });

    return () => observer.disconnect();
  }, [navLinks]);

  // Smooth scroll to section
  const scrollToSection = (sliceType: string) => {
    const section = document.querySelector(`[data-slice-type="${sliceType}"]`);
    if (section) {
      const navbarHeight = 80; // Adjust based on actual navbar height
      const elementPosition = section.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      // Close mobile menu after clicking
      setIsMobileMenuOpen(false);
    }
  };

  // Close mobile menu when clicking outside or on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <nav
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-32px)] max-w-[1040px] transition-all duration-300 ${
        isSticky ? 'top-2' : 'top-4'
      }`}
    >
      <div
        className={`
          backdrop-blur-[47.5px] backdrop-saturate-150
          bg-black/30
          rounded-2xl
          px-6 py-3
          flex items-center justify-between
          transition-all duration-300
          ${isSticky ? 'shadow-lg shadow-black/20' : ''}
        `}
      >
        {/* Logo */}
        <div className="flex-shrink-0">
          {isFilled.image(logo) ? (
            <PrismicNextImage
              field={logo}
              className="h-[29px] w-auto"
              width={129}
              height={29}
            />
          ) : (
            <DevsuLogoComplete width={129} height={29} />
          )}
        </div>

        {/* Navigation Links - Desktop */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollToSection(link.sliceType)}
              className={`
                font-inter font-medium text-base leading-6 
                transition-colors duration-200
                hover:text-[#05F273]
                ${
                  activeSection === link.sliceType
                    ? 'text-[#05F273]'
                    : 'text-white'
                }
              `}
            >
              {link.label}
            </button>
          ))}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-white p-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle mobile menu"
        >
          {isMobileMenuOpen ? <CloseIcon /> : <HamburgerIcon />}
        </button>

        {/* CTA Button - Desktop */}
        {isFilled.link(primary_action) && (
          <PrismicNextLink
            field={primary_action}
            className="
              hidden md:flex
              bg-[#05F273] 
              text-black 
              font-manrope font-bold 
              text-base leading-6
              px-6 py-3 
              rounded-full
              transition-all duration-200
              hover:bg-[#04d966]
              hover:shadow-lg hover:shadow-[#05F273]/20
              flex-shrink-0
            "
          >
            {primary_action.text || 'Book a call'}
          </PrismicNextLink>
        )}
      </div>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div
          className="
            md:hidden
            mt-2
            backdrop-blur-[47.5px] backdrop-saturate-150
            bg-black/30
            rounded-2xl
            px-6 py-4
            flex flex-col gap-4
          "
        >
          {navLinks.map((link) => (
            <button
              key={`mobile-${link.id}`}
              onClick={() => scrollToSection(link.sliceType)}
              className={`
                font-inter font-medium text-base leading-6 
                transition-colors duration-200
                hover:text-[#05F273]
                text-left
                ${
                  activeSection === link.sliceType
                    ? 'text-[#05F273]'
                    : 'text-white'
                }
              `}
            >
              {link.label}
            </button>
          ))}

          {/* CTA Button - Mobile */}
          {isFilled.link(primary_action) && (
            <PrismicNextLink
              field={primary_action}
              className="
                bg-[#05F273] 
                text-black 
                font-manrope font-bold 
                text-base leading-6
                px-6 py-3 
                rounded-full
                transition-all duration-200
                hover:bg-[#04d966]
                text-center
                mt-2
              "
            >
              {primary_action.text || 'Book a call'}
            </PrismicNextLink>
          )}
        </div>
      )}
    </nav>
  );
};

export default NavigationBar;
